import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import {
  cityScrapeApi,
} from '../../api/cityConfig';
import ScraperParameterFields from '../../components/admin/ScraperParameterFields';
import { initialScraperParameters } from '../../utils/scraperParameters';

export default function ScrapeSourceSettings() {
  const { cityId, sourceId } = useParams<{ cityId: string; sourceId: string }>();
  const city = Number(cityId);
  const source = Number(sourceId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [category, setCategory] = useState('hometogo');
  const [parameters, setParameters] = useState<Record<string, unknown>>({});
  const [initializedSource, setInitializedSource] = useState<number | null>(null);

  const sourceQuery = useQuery({
    queryKey: ['city', city, 'scrape-url', source],
    queryFn: () => cityScrapeApi.getScrapeUrl(city, source),
    enabled: Number.isFinite(city) && Number.isFinite(source),
  });
  const categoriesQuery = useQuery({
    queryKey: ['scraper-categories', city],
    queryFn: () => cityScrapeApi.getScraperCategories(city),
    enabled: Number.isFinite(city),
  });

  const categories = categoriesQuery.data ?? [];
  const selectedCategory = categories.find((item) => item.category === category);

  useEffect(() => {
    if (!sourceQuery.data || !categoriesQuery.data || initializedSource === source) return;
    const sourceData = sourceQuery.data;
    const definition = categoriesQuery.data.find(
      (item) => item.category === sourceData.category,
    );
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Initialize the form after both async configuration queries resolve.
    setCategory(sourceData.category);
    setParameters(initialScraperParameters(definition?.parameters ?? [], sourceData.parameters));
    setInitializedSource(source);
  }, [categoriesQuery.data, initializedSource, source, sourceQuery.data]);

  const saveMutation = useMutation({
    mutationFn: () => cityScrapeApi.setScrapeUrlSettings(city, source, category, parameters),
    onSuccess: (saved) => {
      queryClient.setQueryData(['city', city, 'scrape-url', source], saved);
      queryClient.invalidateQueries({ queryKey: ['city', city, 'scrape-urls'] });
      navigate(-1);
    },
  });

  const changeCategory = (nextCategory: string) => {
    const definition = categories.find((item) => item.category === nextCategory);
    setCategory(nextCategory);
    setParameters(initialScraperParameters(definition?.parameters ?? []));
  };

  const updateParameter = (key: string, value: unknown) => {
    setParameters((current) => ({ ...current, [key]: value }));
  };

  if (sourceQuery.isLoading || categoriesQuery.isLoading) {
    return <div className="p-6">Lädt…</div>;
  }
  if (sourceQuery.error || categoriesQuery.error || !sourceQuery.data) {
    return <div className="p-6 text-red-600">Datenquelle konnte nicht geladen werden.</div>;
  }

  return (
    <main className="max-w-3xl mx-auto px-6 py-8 space-y-8">
      <header className="space-y-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="btn btn-text hover:underline text-sm"
        >
          ← Zurück
        </button>
        <div>
          <p className="text-sm text-gray-500">Datenquelle</p>
          <h1 className="text-2xl font-semibold text-gray-900">{sourceQuery.data.label}</h1>
        </div>
      </header>

      <section className="space-y-6 border-t border-gray-200 pt-6">
        <label className="block max-w-md space-y-1.5">
          <span className="text-sm font-medium text-gray-800">Kategorie</span>
          <select
            value={category}
            onChange={(event) => changeCategory(event.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded bg-white"
          >
            {categories.map((item) => (
              <option key={item.category} value={item.category}>{item.label}</option>
            ))}
          </select>
        </label>

        <ScraperParameterFields
          definitions={selectedCategory?.parameters ?? []}
          parameters={parameters}
          onChange={updateParameter}
        />
      </section>

      <footer className="flex justify-end gap-2 border-t border-gray-200 pt-5">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="btn btn-outline"
        >
          Abbrechen
        </button>
        <button
          type="button"
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          className="btn btn-primary"
        >
          {saveMutation.isPending ? 'Speichert…' : 'Speichern'}
        </button>
      </footer>

      {saveMutation.error && (
        <p className="text-sm text-red-600">{saveMutation.error.message}</p>
      )}
    </main>
  );
}
